import { sha256 } from '@noble/hashes/sha2.js';
import type { CoreRead } from '@exylon/generated-contract-kernel/types';
import { CANONICAL_UUID_PATTERN } from '../../runtime/format';
import type { PanelLiveScreenId } from '../runtime-port';

/**
 * El Parte E5 · the prefill bridge contract (DEC-PUENTE). A decision card links to a canonical Panel screen
 * with query parameters; this module is the per-screen contract of those parameters:
 *
 * - `parte` is the bridge token: an opaque, stable id of the card (never a name or a sentence);
 * - `form` names the owner command the screen prepares (the card's linked proposal `form`);
 * - `restaurant` is the restaurant the card was written for;
 * - every other key is a seed the screen's contract admits, written as an id, a canonical code, an integer,
 *   a date or a flag. A key the contract does not list, or a value of another shape, refuses the whole link:
 *   no personal data and no prose can travel in the URL.
 *
 * The parameters are only a request: the screen opens its form with the seeds the owner's revalidation read
 * returns, under the permission of the command the form prepares. The link executes nothing and records nothing.
 */

type ParteBridgeValueKind = 'uuid' | 'uuidList' | 'code' | 'codeList' | 'settingKey' | 'campaignCode' | 'integer' | 'integerList' | 'date' | 'boolean';

const INTEGER = '(?:0|[1-9][0-9]{0,11})';
const PARTE_BRIDGE_VALUE_PATTERNS: Readonly<Record<ParteBridgeValueKind, RegExp>> = Object.freeze({
  uuid: new RegExp(`^${CANONICAL_UUID_PATTERN}$`, 'i'),
  uuidList: new RegExp(`^${CANONICAL_UUID_PATTERN}(?:,${CANONICAL_UUID_PATTERN}){0,49}$`, 'i'),
  code: /^[a-z][a-z0-9_]{0,63}$/,
  codeList: /^[a-z][a-z0-9_]{0,63}(?:,[a-z][a-z0-9_]{0,63}){0,19}$/,
  settingKey: /^[a-z][a-z0-9_]{0,63}(?:\.[a-z][a-z0-9_]{0,63}){0,3}$/,
  campaignCode: /^[A-Z0-9][A-Z0-9_-]{2,31}$/,
  integer: new RegExp(`^${INTEGER}$`),
  integerList: new RegExp(`^${INTEGER}(?:,${INTEGER}){0,49}$`),
  date: /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/,
  boolean: /^(?:true|false)$/,
});

const PARTE_BRIDGE_TOKEN = /^[A-Za-z0-9_-]{8,64}$/;
/** The reserved keys of every bridge link. */
const PARTE_BRIDGE_KEYS = Object.freeze({ token: 'parte', form: 'form', restaurant: 'restaurant' } as const);

type BridgeReadOperation = CoreRead['operation'];

interface ParteBridgeForm {
  /** The owner's revalidation read, under the permission of the command this form prepares. */
  readonly operation: BridgeReadOperation;
  /** How the revalidation read takes the restaurant: as the `restaurantId` path parameter. */
  readonly parameters: Readonly<Record<string, ParteBridgeValueKind>>;
  /** Fixed-purpose forms refuse another topic even when its value is a syntactically valid code. */
  readonly fixedParameters?: Readonly<Record<string, string>>;
  /** Like `fixedParameters`, for a key a link may omit (a card published before the key existed). */
  readonly fixedIfPresent?: Readonly<Record<string, string>>;
  /** A key whose value must be one of a closed list of reviewed codes (a fixed-purpose form with several purposes). */
  readonly allowedValues?: Readonly<Record<string, readonly string[]>>;
  /** Like `allowedValues`, for a key a link may omit: when present, its value must be one of the reviewed codes. */
  readonly allowedIfPresent?: Readonly<Record<string, readonly string[]>>;
  /** «Vienes de El Parte»: the verb of what the manager will confirm on this screen. */
  readonly verb: string;
  /** Another verb when a seed carries a given code (a form with two intents). */
  readonly verbFor?: Readonly<{ parameter: string; values: Readonly<Record<string, string>> }>;
  /** A form that creates: the derived id travels to the owner under this key (never in the URL), unless the
   * link names an existing object with `unless`. The owner answers `already_applied` when it exists. */
  readonly derived?: Readonly<{ parameter: string; unless?: string }>;
}

interface ParteBridgeScreen {
  readonly screen: PanelLiveScreenId;
  readonly path: string;
  readonly label: string;
  readonly forms: Readonly<Record<string, ParteBridgeForm>>;
}

const MARKETING_PUT: Readonly<Record<string, ParteBridgeValueKind>> = Object.freeze({
  definition_id: 'uuid', expected_version: 'integer', mode: 'code', source_definition_id: 'uuid', preset: 'code',
  distribution_kind: 'code', code: 'campaignCode', benefit_kind: 'code', scope_kind: 'code', dish_id: 'uuid',
  amount_minor: 'integer', maximum_discount_minor: 'integer', minimum_subtotal_minor: 'integer', loyalty_tier_id: 'uuid', weekdays: 'integerList',
  total_redemption_limit: 'integer', valid_from_date: 'date', valid_to_date: 'date', valid_to: 'date',
});

/** One row per destination screen. E4 builds a card's link with `parteBridgeHref`, never by hand. */
const PARTE_BRIDGE_SCREENS = Object.freeze({
  promotions: {
    screen: 'marketing', path: '/app/promotions', label: 'Marketing',
    forms: {
      'offers.definition.put': {
        operation: 'offers.bridge.definition-put.revalidate', parameters: MARKETING_PUT, verb: 'Revisa la promoción y guárdala tú',
        derived: { parameter: 'new_definition_id', unless: 'definition_id' },
      },
      'offers.definition.retire': {
        operation: 'offers.bridge.definition-retire.revalidate',
        parameters: { definition_id: 'uuid', expected_version: 'integer', reason_code: 'code' },
        verb: 'Retira la promoción si estás de acuerdo',
      },
    },
  },
  inventory: {
    screen: 'inventory', path: '/app/inventory', label: 'Inventario',
    forms: {
      'inventory.stock-item.reconcile': {
        operation: 'inventory.bridge.stock-item-reconcile.revalidate',
        parameters: { stock_item_id: 'uuid', reason_code: 'code', expected_version: 'integer' },
        verb: 'Cuenta el ingrediente y guarda el recuento con su motivo',
      },
      'inventory.stock-item.open': {
        operation: 'inventory.bridge.stock-item.revalidate', parameters: { stock_item_id: 'uuid', expected_version: 'integer' },
        verb: 'Revisa el ingrediente',
      },
      'inventory.purchase-notice.open': {
        operation: 'inventory.bridge.stock-item.revalidate', parameters: { stock_item_ids: 'uuidList' },
        verb: 'Revisa lo que hay que comprar',
      },
    },
  },
  menu: {
    screen: 'menu', path: '/app/menu', label: 'Menú',
    forms: {
      'catalog.draft.replace': {
        operation: 'catalog.bridge.draft-replace.revalidate',
        parameters: {
          dish_id: 'uuid', dish_ids: 'uuidList', category_id: 'uuid', category_ids: 'uuidList', option_id: 'uuid', stock_item_id: 'uuid',
          field: 'code', diet_code: 'code', preparation_time_minutes: 'integer', is_default: 'boolean', position_in_category: 'integer',
          inventory_rule: 'code', action: 'code', expected_revision: 'integer',
          // El Parte's price proposals: the proposed price in cents, reviewed again by Catalog's price port.
          price_minor: 'integer',
        },
        verb: 'Revisa el cambio en la carta y guárdalo tú',
      },
      // Two intents: a first photo (the default, no `intent`), or «replace» for a dish that already has one.
      'catalog.media.write': {
        operation: 'catalog.bridge.media-write.revalidate', parameters: { dish_ids: 'uuidList', intent: 'code' },
        allowedIfPresent: { intent: ['add', 'replace'] },
        verb: 'Sube la foto de los platos', verbFor: { parameter: 'intent', values: { replace: 'Cambiar la foto del plato' } },
      },
      'catalog.publish': { operation: 'catalog.bridge.publish.revalidate', parameters: { expected_revision: 'integer' }, verb: 'Revisa la carta y publícala' },
    },
  },
  history: {
    screen: 'history', path: '/app/order-history', label: 'Historial',
    forms: {
      'service.history.filter': {
        operation: 'service.bridge.history.revalidate', parameters: { placed_from: 'date', placed_to: 'date', outcomes: 'codeList' },
        verb: 'Revisa estas cuentas',
      },
      'service.history.order': { operation: 'service.bridge.history.revalidate', parameters: { order_id: 'uuid' }, verb: 'Revisa esta cuenta' },
    },
  },
  orders: {
    screen: 'tpv', path: '/app/orders', label: 'TPV',
    forms: {
      'service.table-session.force-close': {
        operation: 'service.bridge.table-session-force-close.revalidate', parameters: { table_ids: 'uuidList', cause_code: 'code' },
        verb: 'Cierra la mesa sin cobrar, con el PIN de un encargado',
      },
    },
  },
  floorTeam: {
    screen: 'servicio', path: '/app/waiterdelivers', label: 'Servicio',
    forms: {
      'workforce.staff-notice.compose': {
        operation: 'workforce.bridge.staff-notice-compose.revalidate',
        parameters: { topic: 'code', severity: 'code', audience: 'code' },
        // The reviewed topics and the one audience the Workforce owner admits (`backend/src/modules/workforce/staff-notice-contracts.ts`):
        // offer the QR when seating, offer dessert when clearing, ask for a review at payment.
        fixedParameters: { severity: 'info' },
        allowedValues: { topic: ['offer_qr_at_seating', 'offer_dessert_at_clearing', 'ask_review_at_payment'] },
        fixedIfPresent: { audience: 'floor_team' },
        verb: 'Revisa el aviso al equipo de sala y confírmalo',
        // One notice per card link: a second tab or a reopened link prepares the same id, which the owner refuses.
        derived: { parameter: 'notice_id' },
      },
    },
  },
  config: {
    screen: 'configuration', path: '/app/config', label: 'Configuración',
    forms: {
      'restaurant.configuration.apply': {
        operation: 'restaurant-configuration.bridge.apply.revalidate',
        parameters: { setting_key: 'settingKey', value_boolean: 'boolean', value_integer: 'integer', value_code: 'code', expected_revision: 'integer' },
        verb: 'Revisa el ajuste y guárdalo tú',
      },
    },
  },
  cards: {
    screen: 'tarjetas', path: '/app/cards', label: 'Tarjetas',
    forms: {
      'relationship.loyalty-program.put': {
        operation: 'relationship.bridge.loyalty-program-put.revalidate',
        parameters: { state: 'code', tier_id: 'uuid', cashback_bps: 'integer', review_bucket: 'code', reward_tier: 'code', count: 'integer', expected_version: 'integer' },
        verb: 'Revisa el programa de tarjetas y guárdalo tú',
      },
    },
  },
  constructor: {
    screen: 'constructor', path: '/app/constructor', label: 'Constructor',
    forms: {
      'restaurant-configuration.floor.open': {
        operation: 'restaurant-configuration.bridge.floor.revalidate', parameters: { floor_id: 'uuid', table_ids: 'uuidList', expected_revision: 'integer' },
        verb: 'Revisa la sala',
      },
      'workforce.work-target-assignment.bulk-apply': {
        operation: 'workforce.bridge.work-target-assignment-bulk-apply.revalidate', parameters: { employment_ids: 'uuidList', table_ids: 'uuidList' },
        verb: 'Asigna los puestos en el plano y guarda tú el reparto',
      },
    },
  },
  employees: {
    screen: 'hr', path: '/app/employees', label: 'RRHH',
    forms: {
      'workforce.feedback.create': {
        operation: 'workforce.bridge.feedback-create.revalidate', parameters: { employment_id: 'uuid', severity: 'code', review_tag: 'code' },
        verb: 'Escribe tú el aviso y envíalo si estás de acuerdo', derived: { parameter: 'feedback_id' },
      },
    },
  },
} as const satisfies Record<string, ParteBridgeScreen>);

type ParteBridgeDestination = keyof typeof PARTE_BRIDGE_SCREENS;

/** The card destinations of `backend/src/modules/parte/compiler/linked-proposal.ts` and the rows each may land on:
 * the row is the one that prepares the card's form (Historial's forced close runs at the TPV). E3's `dishes`
 * names `/app/dishes`, which is Cocina: the dish editor is Menú's. */
const PARTE_BRIDGE_DESTINATION_ALIASES: Readonly<Record<string, readonly ParteBridgeDestination[]>> = Object.freeze({
  promotions: ['promotions'], menu: ['menu'], dishes: ['menu'], inventory: ['inventory'], order_history: ['history', 'orders'],
  orders: ['orders'], employees: ['employees', 'floorTeam'], cards: ['cards'], config: ['config'], floor_plan: ['constructor'],
});

export const parteBridgeScreenRow = (screen: PanelLiveScreenId): ParteBridgeScreen | null =>
  (Object.values(PARTE_BRIDGE_SCREENS) as ParteBridgeScreen[]).find((row) => row.screen === screen) ?? null;

export type ParteBridgeRequest = Readonly<{
  token: string;
  form: string;
  restaurantId: string;
  operation: BridgeReadOperation;
  verb: string;
  /** The seeds as text, exactly as the owner's revalidation read takes them. */
  parameters: Readonly<Record<string, string>>;
}>;

type ParteBridgeParse =
  | Readonly<{ kind: 'none' }>
  | Readonly<{ kind: 'invalid'; reason: 'invalid_link' | 'restaurant_mismatch' }>
  | Readonly<{ kind: 'request'; request: ParteBridgeRequest }>;

/** Reads a screen's query string. Absent `parte` → no bridge; anything off-contract → an invalid link. */
export function parseParteBridge(search: string, screen: PanelLiveScreenId, restaurantId: string): ParteBridgeParse {
  const query = new URLSearchParams(search);
  const token = query.get(PARTE_BRIDGE_KEYS.token);
  if (token === null) return { kind: 'none' };
  const row = parteBridgeScreenRow(screen);
  const formName = query.get(PARTE_BRIDGE_KEYS.form) ?? '';
  const form = row && Object.hasOwn(row.forms, formName) ? row.forms[formName]! : null;
  const restaurant = query.get(PARTE_BRIDGE_KEYS.restaurant) ?? '';
  if (!row || !form || !PARTE_BRIDGE_TOKEN.test(token) || !PARTE_BRIDGE_VALUE_PATTERNS.uuid.test(restaurant)) return { kind: 'invalid', reason: 'invalid_link' };
  const parameters: Record<string, string> = {};
  const reserved = new Set<string>(Object.values(PARTE_BRIDGE_KEYS));
  for (const [key, value] of query) {
    if (reserved.has(key)) continue;
    const kind = Object.hasOwn(form.parameters, key) ? form.parameters[key]! : null;
    if (!kind || Object.hasOwn(parameters, key) || !PARTE_BRIDGE_VALUE_PATTERNS[kind].test(value)) return { kind: 'invalid', reason: 'invalid_link' };
    parameters[key] = value;
  }
  if (form.fixedParameters && Object.entries(form.fixedParameters).some(([key, value]) => parameters[key] !== value)) {
    return { kind: 'invalid', reason: 'invalid_link' };
  }
  if (form.fixedIfPresent && Object.entries(form.fixedIfPresent).some(([key, value]) => Object.hasOwn(parameters, key) && parameters[key] !== value)) {
    return { kind: 'invalid', reason: 'invalid_link' };
  }
  if (form.allowedValues && Object.entries(form.allowedValues).some(([key, values]) => !values.includes(parameters[key] ?? ''))) {
    return { kind: 'invalid', reason: 'invalid_link' };
  }
  if (form.allowedIfPresent && Object.entries(form.allowedIfPresent).some(([key, values]) => Object.hasOwn(parameters, key) && !values.includes(parameters[key]!))) {
    return { kind: 'invalid', reason: 'invalid_link' };
  }
  if (restaurant.toLowerCase() !== restaurantId.toLowerCase()) return { kind: 'invalid', reason: 'restaurant_mismatch' };
  const base = { token, form: formName, restaurantId: restaurant.toLowerCase() };
  if (form.derived && !(form.derived.unless && Object.hasOwn(parameters, form.derived.unless))) parameters[form.derived.parameter] = parteBridgeDerivedId(base);
  const verb = (form.verbFor && form.verbFor.values[parameters[form.verbFor.parameter] ?? '']) || form.verb;
  return { kind: 'request', request: { ...base, operation: form.operation, verb, parameters } };
}

/** The same path without the bridge (the form was confirmed, or the manager dismissed the link). A bridge
 * link carries only bridge keys, so nothing of the screen's own state is dropped. */
export function stripParteBridge(search: string): string {
  return new URLSearchParams(search).has(PARTE_BRIDGE_KEYS.token) ? '' : search;
}

type ParteLinkedProposalJson = Readonly<{
  destination: string;
  form: string;
  parameters: Readonly<Record<string, string | number | boolean | null | readonly (string | number)[]>>;
}>;

/** The screen row that prepares a linked proposal's form, or null when no screen prepares it. */
export function parteBridgeProposalRow(proposal: Pick<ParteLinkedProposalJson, 'destination' | 'form'>): ParteBridgeScreen | null {
  const candidates = Object.hasOwn(PARTE_BRIDGE_DESTINATION_ALIASES, proposal.destination) ? PARTE_BRIDGE_DESTINATION_ALIASES[proposal.destination]! : [];
  return candidates.map((key) => PARTE_BRIDGE_SCREENS[key] as ParteBridgeScreen).find((candidate) => Object.hasOwn(candidate.forms, proposal.form)) ?? null;
}

const valueText = (value: string | number | boolean | readonly (string | number)[]): string =>
  Array.isArray(value) ? value.join(',') : String(value);

/**
 * The link of a card's linked proposal (`exylon.parte.linked-proposal@1.0`), for E4. `token` is the card's
 * stable opaque id: the same card opened twice yields the same link, so the same derived ids.
 * Returns null when the proposal is off-contract; the card then shows no link rather than a broken one.
 * @public E4 wires it into the decision card (roadmap E5: the link from the card waits for E4).
 */
export function parteBridgeHref(proposal: ParteLinkedProposalJson, token: string, restaurantId: string): string | null {
  const row = parteBridgeProposalRow(proposal);
  if (!row || !PARTE_BRIDGE_TOKEN.test(token)) return null;
  const query = new URLSearchParams({ [PARTE_BRIDGE_KEYS.token]: token, [PARTE_BRIDGE_KEYS.form]: proposal.form, [PARTE_BRIDGE_KEYS.restaurant]: restaurantId });
  for (const [key, value] of Object.entries(proposal.parameters)) if (value !== null) query.set(key, valueText(value));
  const href = `${row.path}?${query.toString()}`;
  return parseParteBridge(`?${query.toString()}`, row.screen, restaurantId).kind === 'request' ? href : null;
}

const hex = (bytes: Uint8Array): string => Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

/**
 * A name-based UUID for what the form creates, from the restaurant, the token and the form: two tabs (or a
 * reopened link) prepare the same id, so the owner's create-once rule refuses the second confirmation.
 */
export function parteBridgeDerivedId(request: Pick<ParteBridgeRequest, 'restaurantId' | 'token' | 'form'>, purpose = 'object'): string {
  const bytes = sha256(new TextEncoder().encode(`exylon.parte.bridge:${request.restaurantId}:${request.token}:${request.form}:${purpose}`)).slice(0, 16);
  bytes[6] = (bytes[6]! & 0x0f) | 0x50;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const text = hex(bytes);
  return `${text.slice(0, 8)}-${text.slice(8, 12)}-${text.slice(12, 16)}-${text.slice(16, 20)}-${text.slice(20)}`;
}
